import { useEffect, useState } from 'react'
import { api, type SynonymCandidate } from '../lib/api.ts'

/**
 * Approving synonym candidates.
 *
 * Small screen, disproportionate value: an approved synonym is retrievable
 * from the next verification onward and stays that way. A fuzzy match helps one
 * verification; an synonym helps every verification of that ingredient forever.
 *
 * Open to any pharmacist rather than to admins. The person who just resolved
 * an ingredient is the one who knows whether two names mean the same
 * substance, and routing that through an admin would lose the judgement.
 */
export function Synonyms({ onBack }: { onBack: () => void }) {
  const [candidates, setCandidates] = useState<SynonymCandidate[]>([])
  const [busy, setBusy] = useState<string | undefined>()
  const [error, setError] = useState<string | undefined>()

  async function load() {
    try {
      setCandidates((await api.synonyms()).candidates)
      setError(undefined)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'falha ao carregar')
    }
  }

  useEffect(() => {
    void load()
  }, [])

  async function act(id: string, action: 'approve' | 'reject') {
    setBusy(id)
    try {
      if (action === 'approve') await api.approveSynonym(id)
      else await api.rejectSynonym(id)
      await load()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'falha ao registrar')
    } finally {
      setBusy(undefined)
    }
  }

  return (
    <main className="queue">
      <header className="queue__head">
        <button type="button" className="link" onClick={onBack}>
          ← Fila
        </button>
        <h1>Sinônimos pendentes</h1>
      </header>

      <p className="muted">
        Um sinônimo aprovado passa a valer na próxima conferência, para todas as receitas desta
        farmácia.
      </p>

      {error !== undefined && <p className="error">{error}</p>}
      {candidates.length === 0 && <p className="muted">Nenhum sinônimo aguardando aprovação.</p>}

      <table className="queue__table">
        <thead>
          <tr>
            <th>Termo da receita</th>
            <th>Produto no catálogo</th>
            <th>Origem</th>
            <th aria-label="Ações" />
          </tr>
        </thead>
        <tbody>
          {candidates.map((candidate) => (
            <tr key={candidate.id}>
              <td>
                <strong>{candidate.term}</strong>
              </td>
              <td>
                {candidate.productName} <span className="muted">({candidate.erpCode})</span>
              </td>
              <td>
                <span className="badge">
                  {candidate.source === 'pharmacist' ? 'Farmacêutico' : 'Sugerido'}
                </span>
              </td>
              <td>
                <div className="finding__actions">
                  <button
                    type="button"
                    disabled={busy === candidate.id}
                    onClick={() => act(candidate.id, 'approve')}
                  >
                    Aprovar
                  </button>
                  <button
                    type="button"
                    disabled={busy === candidate.id}
                    onClick={() => act(candidate.id, 'reject')}
                  >
                    Descartar
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  )
}
